/**
 * Build Cylindo viewer / Content API parameters from product custom attributes
 * and optional Cylindo image URLs (may include feature=… and background=… in any combination).
 */

/** King Living shared Cylindo customer id (fallback when not present on image URL) */
export const DEFAULT_CYLINDO_CUSTOMER_ID = '4973';

/**
 * Normalize a Cylindo product code (= `c_cylindoId`) that *may* arrive
 * already URL-encoded from PIM/SFCC (some upstream exports encode spaces
 * and special characters before the value lands on the SCAPI custom
 * attribute, e.g. `MYCO-SD%20TBL%20TIMB%20490%20LTH------`).
 *
 * If we then `encodeURIComponent` such a string a second time when we
 * build the Content API URL, every `%` becomes `%25` and the path turns
 * into `MYCO-SD%2520TBL%2520...`, which Cylindo cannot resolve and the
 * image fails to load.
 *
 * Idempotently returns the *decoded* form (also trimmed) so downstream
 * callers can safely encode exactly once. Cheap probe (`%XX` regex)
 * avoids touching codes that don't need it; falls back to the trimmed
 * value on malformed escapes (`decodeURIComponent` would throw).
 * Returns `''` for `null`/`undefined`/empty input so existing falsy
 * checks (`if (!productCode) return null;`) keep working.
 *
 * @param {*} code
 * @returns {string}
 */
export function normalizeCylindoCode(code) {
  if (code == null) return '';

  const trimmed = String(code).trim();

  if (!trimmed) return '';
  if (!/%[0-9A-Fa-f]{2}/.test(trimmed)) return trimmed;

  try {
    return decodeURIComponent(trimmed);
  } catch {
    return trimmed;
  }
}

/**
 * Product code for Cylindo API/viewer — master / main product only.
 * Never use cover or option `cylindoId` as the product code (DE-790).
 *
 * @param {Object|null|undefined} masterProduct
 * @returns {string|null}
 */
export function resolveCylindoProductCode(masterProduct) {
  const raw = masterProduct?.c_cylindoId ?? masterProduct?.cylindoId;
  const normalized = raw ? normalizeCylindoCode(String(raw).trim()) : '';

  return normalized || null;
}

/**
 * @param {string} link
 * @returns {boolean}
 */
function isCylindoUrl(link) {
  if (!link || typeof link !== 'string') return false;

  try {
    const host = new URL(link, 'https://content.cylindo.com').hostname;

    return host.includes('cylindo.com');
  } catch {
    return false;
  }
}

/**
 * @param {string} entry — e.g. "UPHOLSTERY:BX-28" or "TIMBER SHELVES VENEERS:MS_AW"
 * @returns {string} stable key for merge/override (text before first ":")
 */
function featureEntryKey(entry) {
  const t = String(entry).trim();

  if (!t) return '';
  const i = t.indexOf(':');

  return i === -1 ? t : t.slice(0, i).trim();
}

/**
 * Query segment for `feature=…` on Cylindo Content API.
 * The colon between feature code and option must stay literal (`KEY:VALUE`), not `%3A`
 * from encoding the whole string — otherwise the API rejects the URL.
 *
 * @param {string} feature - e.g. "UPHOLSTERY:LX-96", "TIMBER SHELVES VENEERS:MS_SM"
 * @returns {string} value to place after `feature=` (already safe for query string)
 */
export function formatCylindoFeatureQueryPair(feature) {
  const t = String(feature).trim();

  if (!t) return '';
  const i = t.indexOf(':');

  if (i === -1) return encodeURIComponent(t);
  const key = t.slice(0, i).trim();
  const value = t.slice(i + 1).trim();

  return `${encodeURIComponent(key)}:${encodeURIComponent(value)}`;
}

/**
 * Later entries override earlier ones for the same feature key.
 *
 * @param {string[]} urlFeatures — from URL searchParams.getAll('feature')
 * @param {string[]} attrFeatures — from c_cylindoFeature
 * @returns {string} Cylindo "configuration" string (comma-separated), or '' when none
 */
export function mergeCylindoConfiguration(urlFeatures = [], attrFeatures = []) {
  const map = new Map();

  for (const f of urlFeatures) {
    const t = String(f).trim();

    if (!t) continue;
    const k = featureEntryKey(t);

    if (k) map.set(k, t);
  }

  for (const f of attrFeatures) {
    const t = String(f).trim();

    if (!t) continue;
    const k = featureEntryKey(t);

    if (k) map.set(k, t);
  }

  const merged = [...map.values()].filter(Boolean);

  return merged.length ? merged.join(', ') : '';
}

/**
 * Parse a bracketed `cylindoFeature` list.
 *
 * Part of the catalog stores the list Python-style with single quotes
 * (`['UPHOLSTERY:LE-01']`) rather than JSON (`["UPHOLSTERY:LE-01"]`) — a strict `JSON.parse`
 * rejects those and silently throws away a perfectly valid feature, which then renders the
 * product's *default* Cylindo model instead of the selected cover. The retry only runs when the
 * value contains no double quotes, so well-formed JSON is never rewritten.
 *
 * @param {string} str - trimmed value starting with `[`
 * @returns {Array|null} parsed array, or null when unparseable
 */
function parseCylindoFeatureList(str) {
  try {
    const parsed = JSON.parse(str);

    return Array.isArray(parsed) ? parsed : null;
  } catch {
    if (str.includes('"')) return null;

    try {
      const parsed = JSON.parse(str.replace(/'/g, '"'));

      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}

/**
 * @param {Object} [product]
 * @returns {string[]}
 */
export function parseCylindoFeatureAttribute(product) {
  const raw = product?.c_cylindoFeature ?? product?.cylindoFeature;

  if (raw == null || raw === '') return [];

  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }

  const str = String(raw).trim();

  if (str.startsWith('[')) {
    const parsed = parseCylindoFeatureList(str);

    return Array.isArray(parsed) ? parsed.map((x) => String(x).trim()).filter(Boolean) : [];
  }

  return str.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Normalize cylindoFeature value (array, string, or JSON-stringified array) to a comma-separated
 * string suitable for Cylindo API, or null when empty/invalid.
 *
 * @param {*} raw - Raw cylindoFeature value (can be array, string, null, undefined)
 * @returns {string|null} - Comma-separated feature string or null
 *
 * @example
 * // Empty values → null
 * normalizeCylindoFeatureToString(null) → null
 * normalizeCylindoFeatureToString(undefined) → null
 * normalizeCylindoFeatureToString('') → null
 * normalizeCylindoFeatureToString(' ') → null
 * normalizeCylindoFeatureToString([]) → null
 * normalizeCylindoFeatureToString(['']) → null
 * normalizeCylindoFeatureToString([' ']) → null
 * normalizeCylindoFeatureToString('[]') → null
 * normalizeCylindoFeatureToString('[""]') → null
 *
 * // Valid values → normalized string
 * normalizeCylindoFeatureToString(['TIMBER:A']) → 'TIMBER:A'
 * normalizeCylindoFeatureToString(['A', 'B']) → 'A, B'
 * normalizeCylindoFeatureToString('["TIMBER:A"]') → 'TIMBER:A'
 * normalizeCylindoFeatureToString("['TIMBER:A']") → 'TIMBER:A'
 * normalizeCylindoFeatureToString('TIMBER:A') → 'TIMBER:A'
 * normalizeCylindoFeatureToString('TIMBER:A, LEGS:B') → 'TIMBER:A, LEGS:B'
 */
export function normalizeCylindoFeatureToString(raw) {
  // Handle null, undefined, empty string
  if (raw == null || raw === '') {
    return null;
  }

  // Handle array directly (including empty arrays or arrays with empty strings)
  if (Array.isArray(raw)) {
    const items = raw.map((x) => String(x).trim()).filter(Boolean);

    return items.length > 0 ? items.join(', ') : null;
  }

  const str = String(raw).trim();

  // Handle empty string after trim (e.g., " ", "  ")
  if (!str) {
    return null;
  }

  // Handle stringified array (e.g., "[]", "[\"VALUE\"]", "[\"\"]", "['VALUE']")
  if (str.startsWith('[')) {
    const parsed = parseCylindoFeatureList(str);

    // Unparseable → not a valid feature
    if (!Array.isArray(parsed)) return null;

    const items = parsed.map((x) => String(x).trim()).filter(Boolean);

    return items.length > 0 ? items.join(', ') : null;
  }

  // Handle comma-separated string
  const items = str.split(',').map((s) => s.trim()).filter(Boolean);

  return items.length > 0 ? items.join(', ') : null;
}

/**
 * Parse customer id and product code from a Cylindo content API image URL.
 *
 * @param {string} link
 * @returns {{ customerId: string, productCode: string }|null}
 */
export function parseCylindoProductFromImageUrl(link) {
  if (!isCylindoUrl(link)) return null;

  try {
    const url = new URL(link);
    const pathParts = url.pathname.split('/').filter(Boolean);

    if (pathParts.length < 5 || pathParts[0] !== 'api' || pathParts[1] !== 'v2') {
      return null;
    }

    return {
      customerId: pathParts[2],
      productCode: decodeURIComponent(pathParts[4]),
    };
  } catch {
    return null;
  }
}

/**
 * @param {Object} [heroImageGroup]
 * @param {Array} [imageGroups]
 * @returns {string|null} first usable Cylindo image link from hero, else first Cylindo group in list
 */
export function getFirstCylindoImageLink(heroImageGroup, imageGroups = []) {
  if (heroImageGroup?.images?.length) {
    for (const img of heroImageGroup.images) {
      if (img?.mediaType === 'video') continue;
      if (img?.link && isCylindoUrl(img.link)) return img.link;
    }
  }

  const cylindoGroup = imageGroups.find(
    (g) => g.imageSource === 'cylindo' && g.images?.length > 0
  );
  const first = cylindoGroup?.images?.[0];

  if (first?.link && isCylindoUrl(first.link)) return first.link;

  return null;
}

/**
 * Master product GET only includes master-level `c_cylindoId` / `c_cylindoFeature`.
 * SFRA adds per-variant `cylindoFeature` and lifestyle stills (`pimImages`
 * payload = `pimlyLifeStyleImages`) on each `c_allVariantsData` row. Merge the matching row so:
 *
 * - Cover `cylindoFeature` is applied; cover `cylindoId` is never copied onto the master
 *   (DE-790 — viewer product code stays `master.c_cylindoId`).
 * - When the variant has own lifestyle stills, a variant-specific PIM image group is injected
 *   into `c_processedImageGroups` (with `variationAttributes`) so `findImageGroupBy`
 *   resolves it with higher priority than any Cylindo group for the same variant.
 *
 * `match.pimImages` is already a pre-built array of image/video objects produced by
 * SFRA's `coverVariantsHelper` (same format as `imageHelper.buildPrioritizedImageGroups`),
 * so no further path processing is needed on the PWA side.
 *
 * Image source priority for a selected variant (mirrors SFRA spec):
 *   1. Own lifestyle stills (`pimImages` payload from pimlyLifeStyleImages)
 *   2. Variant Cylindo     (already in c_processedImageGroups from SFRA)
 *   3. Master images       (fallback when no variant-specific group matches)
 *
 * @param {Object} product - Shopper product (may include c_allVariantsData, c_processedImageGroups)
 * @param {Object} selectedVariationAttributes - attribute id → value (same keys as variationValues in SFRA)
 * @returns {Object} product clone with c_cylindoFeature and c_processedImageGroups updated
 */
export function mergeProductWithVariantCylindo(product, selectedVariationAttributes) {
  if (!product || !selectedVariationAttributes || typeof selectedVariationAttributes !== 'object') {
    return product;
  }

  const keys = Object.keys(selectedVariationAttributes).filter(
    (k) => selectedVariationAttributes[k] != null && selectedVariationAttributes[k] !== ''
  );

  if (keys.length === 0) {
    return product;
  }

  let list;

  try {
    const raw = product.c_allVariantsData;

    if (!raw) return product;
    list = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return product;
  }

  if (!Array.isArray(list)) return product;

  const match = list.find((entry) => {
    const vv = entry?.variationValues;

    if (!vv || typeof vv !== 'object') return false;

    return keys.every((k) => String(vv[k]) === String(selectedVariationAttributes[k]));
  });

  if (!match) return product;

  const next = {...product};

  if (match.cylindoFeature != null && match.cylindoFeature !== '') {
    const normalized = normalizeCylindoFeatureToString(match.cylindoFeature);

    if (normalized) {
      next.c_cylindoFeature = normalized;
    }
  }

  // Inject variant-specific PIM image groups when the cover has own lifestyle stills.
  // match.pimImages is SFRA-built from pimlyLifeStyleImages (proxy paths, alt, title).
  // Groups carry variationAttributes for findImageGroupBy and imageSource:'pim'.
  if (Array.isArray(match.pimImages) && match.pimImages.length > 0) {
    // Build variationAttributes in the same shape SFRA uses (array of {id, values:[{value}]})
    const variationAttributes = keys.map((k) => ({
      id: k,
      values: [{value: String(selectedVariationAttributes[k])}],
    }));

    let processedData = null;

    try {
      const raw = next.c_processedImageGroups;

      processedData = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null;
    } catch {
      processedData = null;
    }

    const existingGroups = processedData?.imageGroups ?? [];

    // Build one PIM group per view type that already exists in the processed groups,
    // so the injected groups cover the same view types the gallery uses (large, hi-res, etc.)
    const viewTypes = [...new Set(existingGroups.map((g) => g.viewType))];

    if (viewTypes.length === 0) {
      viewTypes.push('large');
    }

    const injectedGroups = viewTypes.map((vt) => ({
      viewType: vt,
      images: match.pimImages,
      imageSource: 'pim',
      variationAttributes,
    }));

    // Prepend injected PIM groups so findImageGroupBy (which uses Array.find) returns
    // the PIM group before any Cylindo group for the same variationAttributes.
    next.c_processedImageGroups = JSON.stringify({
      imageGroups: [...injectedGroups, ...existingGroups],
    });
  }

  return next;
}

/**
 * Extract params for Cylindo viewer and Content API checks.
 *
 * - `code` / product id: **c_cylindoId** (required for viewer when set); else derived from image URL.
 * - `customerId`: c_cylindoCustomerId if set, else from image URL path, else {@link DEFAULT_CYLINDO_CUSTOMER_ID}.
 * - `configuration`: merge URL `feature` params from the **hero** Cylindo image (if any) with **c_cylindoFeature**
 *   (attribute wins on same feature key).
 * - `background`: from URL when present (optional passthrough to viewer).
 *
 * @param {Object} product
 * @param {Array} [imageGroups]
 * @param {Object} [heroImageGroup] — resolved large / variant hero group from ImageGallery
 * @param {Object} [options]
 * @param {string|number} [options.siteCylindoCustomerId] — site preference (MRT), used before default 4973
 * @returns {Object|null} { customerId, productCode, configuration?, background? }
 */
export function extractCylindoParams(product, imageGroups = [], heroImageGroup = null, options = {}) {
  if (!product) return null;

  const link = getFirstCylindoImageLink(heroImageGroup, imageGroups);

  let customerId =
    product.c_cylindoCustomerId != null && product.c_cylindoCustomerId !== '' ?
      String(product.c_cylindoCustomerId).trim() :
      product.cylindoCustomerId != null && product.cylindoCustomerId !== '' ?
        String(product.cylindoCustomerId).trim() :
        null;

  let productCode = resolveCylindoProductCode(product);

  let urlFeatures = [];
  let background;

  if (link) {
    try {
      const url = new URL(link);
      const parsed = parseCylindoProductFromImageUrl(link);

      if (parsed) {
        if (!customerId) customerId = parsed.customerId;
        if (!productCode) productCode = parsed.productCode;
      }

      urlFeatures = url.searchParams.getAll('feature');
      const bg = url.searchParams.get('background');

      if (bg) background = bg;
    } catch {
      // ignore malformed URL
    }
  }

  if (!productCode && link) {
    const parsed = parseCylindoProductFromImageUrl(link);

    if (parsed) productCode = parsed.productCode;
  }

  if (!productCode) return null;

  if (!customerId) {
    const siteId = options.siteCylindoCustomerId;

    if (siteId != null && siteId !== '') {
      customerId = String(siteId).trim();
    }
  }

  if (!customerId) customerId = DEFAULT_CYLINDO_CUSTOMER_ID;

  const attrFeatures = parseCylindoFeatureAttribute(product);
  const configuration = mergeCylindoConfiguration(urlFeatures, attrFeatures);

  const result = {
    customerId,
    productCode,
    configuration,
  };

  if (background) result.background = background;

  return result;
}

/**
 * Build a static Cylindo Content API image URL for the current product configuration.
 * Used for cart/order line items (c_selectedOptionsUI.cylindoUrl) so the configured
 * product image can be shown in cart, order confirmation, and emails.
 *
 * The product code comes from the **master** product's `c_cylindoId` (the package code
 * that Cylindo uses to identify the 3D model, e.g. "J2-PKG 9").  Variant-level
 * `cylindoId` values are material-specific codes and must NOT be used as the
 * Content API product code.
 *
 * Features/configuration come from the **variant** product's `c_cylindoFeature`
 * (or `cylindoFeature` in c_allVariantsData entries), which tells Cylindo which
 * upholstery, timber veneer, etc. to render.
 *
 * URL format: https://content.cylindo.com/api/v2/{customerId}/products/{code}/frames/1/{code}.webp?features=...
 *
 * @param {Object} masterProduct - Master/parent product (supplies `c_cylindoId`)
 * @param {Object} [variantProduct] - Selected variant (supplies `c_cylindoFeature` / `cylindoFeature`).
 *   When omitted the master's own features are used.
 * @param {Object} [options]
 * @param {string|number} [options.customerId] - Cylindo customer ID override
 * @param {number} [options.frame] - Frame number (default 1)
 * @returns {string|null} Static image URL or null when Cylindo attributes are missing
 */
export function buildCylindoImageUrl(masterProduct, variantProduct = null, options = {}) {
  if (!masterProduct) return null;

  const productCode = resolveCylindoProductCode(masterProduct);

  if (!productCode) return null;

  const customerId =
    options.customerId ?? masterProduct.c_cylindoCustomerId ?? DEFAULT_CYLINDO_CUSTOMER_ID;
  const frame = options.frame ?? 1;

  const featureSource = variantProduct ?? masterProduct;
  const features = parseCylindoFeatureAttribute(featureSource);
  // Normalize first so a pre-encoded value (e.g. spaces stored as `%20`) is
  // decoded once before we re-encode — otherwise the path ends up with
  // `%25...` (double encoding) and Cylindo returns 404.
  const encodedCode = encodeURIComponent(normalizeCylindoCode(String(productCode).trim()));

  let url = `https://content.cylindo.com/api/v2/${customerId}/products/${encodedCode}/frames/${frame}/${encodedCode}.webp`;

  if (features.length > 0) {
    const featureParams = features.map((f) => `features=${encodeURIComponent(f)}`).join('&');

    url += `?${featureParams}`;
  }

  return url;
}

// ── Gallery helpers ────────────────────────────────────────────────────────────

/**
 * Parse `c_processedImageGroups` from a product. Returns [] on missing/malformed data.
 * @param {Object|null} product
 * @returns {Array}
 */
export function parseProcessedImageGroups(product) {
  if (!product?.c_processedImageGroups) return [];

  try {
    const raw = product.c_processedImageGroups;
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return parsed?.imageGroups && Array.isArray(parsed.imageGroups) ? parsed.imageGroups : [];
  } catch {
    return [];
  }
}

/**
 * Look up the matched variant entry in `c_allVariantsData` by selected variation attributes.
 * Used to read variant-level Cylindo data (id, feature, customerId, lifestyle stills, …) without
 * cloning the master product. Returns null when no match is found or data is missing/malformed.
 *
 * @param {Object|null} product
 * @param {Object} selectedVariationAttributes - attribute id → value (same keys as variationValues in SFRA)
 * @returns {Object|null} the matched variant entry, or null
 */
export function findVariantEntry(product, selectedVariationAttributes) {
  if (!product || !selectedVariationAttributes || typeof selectedVariationAttributes !== 'object') {
    return null;
  }

  const keys = Object.keys(selectedVariationAttributes).filter(
    (k) => selectedVariationAttributes[k] != null && selectedVariationAttributes[k] !== ''
  );

  if (keys.length === 0) return null;

  let list;

  try {
    const raw = product.c_allVariantsData;

    if (!raw) return null;
    list = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }

  if (!Array.isArray(list)) return null;

  return (
    list.find((entry) => {
      const vv = entry?.variationValues;

      if (!vv || typeof vv !== 'object') return false;

      return keys.every((k) => String(vv[k]) === String(selectedVariationAttributes[k]));
    }) ?? null
  );
}

/**
 * Look up the `c_cylindoFeature` string for the matched variant in `c_allVariantsData`.
 * Returns null when no match is found or data is missing/malformed.
 * @param {Object|null} product
 * @param {Object} selectedVariationAttributes
 * @returns {string|null}
 */
export function findVariantCylindoFeature(product, selectedVariationAttributes) {
  const match = findVariantEntry(product, selectedVariationAttributes);

  if (!match || match.cylindoFeature == null || match.cylindoFeature === '') return null;

  return normalizeCylindoFeatureToString(match.cylindoFeature);
}

// ── AR ─────────────────────────────────────────────────────────────────────────

/**
 * Build a Cylindo ARQR URL from already-resolved params.
 * Format: https://content.cylindo.com/api/v2/{customerId}/products/{productCode}/ARQR/{filename}
 *   ?feature={key}:{value}&redirect_url={url}
 *
 * @param {string} cylindoId   - Product code (= c_cylindoId)
 * @param {string} customerId  - Cylindo customer ID
 * @param {string} [configuration] - Comma-separated feature string, e.g. "UPHOLSTERY:BX-02"
 * @returns {string}
 */
export function buildCylindoArqrUrl(cylindoId, customerId, configuration) {
  // See `normalizeCylindoCode` — guards against pre-encoded values being
  // re-encoded by `encodeURIComponent` (would yield `%25` instead of `%`).
  const normalizedId = normalizeCylindoCode(cylindoId);
  const filename = encodeURIComponent(normalizedId);
  let url = `https://content.cylindo.com/api/v2/${customerId}/products/${encodeURIComponent(normalizedId)}/ARQR/${filename}`;

  const queryParams = [];

  if (configuration) {
    configuration.split(',').map((f) => f.trim()).filter(Boolean).forEach((f) => {
      const pair = formatCylindoFeatureQueryPair(f);

      if (pair) queryParams.push(`feature=${pair}`);
    });
  }

  if (typeof window !== 'undefined') {
    queryParams.push(`redirect_url=${encodeURIComponent(window.location.href)}`);
  }

  if (queryParams.length > 0) url += `?${queryParams.join('&')}`;

  return url;
}

/**
 * Thumbnail / hero still from Cylindo Content API **Frames** (not ARQR).
 * @see https://pdp.cylindo.com/API/content-api — `frames/{frame}/{filename}` + `feature` query params.
 *
 * @param {string} cylindoId - Product code (= c_cylindoId)
 * @param {string} customerId
 * @param {string} [configuration] - Comma-separated merged features, e.g. "UPHOLSTERY:BX-02, LEGS:METAL"
 * @param {{
 *   size?: number,
 *   encoding?: string,
 *   background?: string,
 *   smartCrop?: boolean,
 * }} [options] - Always uses frame **1** in the path. Omit `encoding` in query when unused — format is inferred from `.webp` in the path (matches Cylindo examples).
 * @returns {string}
 */
export function buildCylindoSnapshotImageUrl(cylindoId, customerId, configuration, options = {}) {
  const {background, encoding, size = 1024, smartCrop} = options;
  const frame = 1;

  // Normalize once so subsequent `encodeURIComponent` does not double-encode
  // a pre-encoded value (see `normalizeCylindoCode`).
  const normalizedId = normalizeCylindoCode(cylindoId);
  const code = encodeURIComponent(normalizedId);
  const fileBase = String(normalizedId).trim().replace(/[/\\?*]/g, '_') || 'product';
  const fileName = encodeURIComponent(`${fileBase}.webp`);

  let url = `https://content.cylindo.com/api/v2/${customerId}/products/${code}/frames/${frame}/${fileName}`;

  const queryParams = [];

  if (size != null) queryParams.push(`size=${encodeURIComponent(String(size))}`);

  if (configuration) {
    configuration.split(',').map((f) => f.trim()).filter(Boolean).forEach((f) => {
      const pair = formatCylindoFeatureQueryPair(f);

      if (pair) queryParams.push(`feature=${pair}`);
    });
  }

  if (background) queryParams.push(`background=${encodeURIComponent(background)}`);
  if (smartCrop === true) queryParams.push('smartCrop=true');
  // Path ends in .webp — do not add encoding=webp to query (matches valid Cylindo frame URLs).
  if (encoding && String(encoding).toLowerCase() !== 'webp') {
    queryParams.push(`encoding=${encodeURIComponent(encoding)}`);
  }

  if (queryParams.length > 0) url += `?${queryParams.join('&')}`;

  return url;
}
